/**
 * DieselDashboardPage.tsx
 * "Diesel Overview" — matches the redesign's #screen-dashboard: 3 KPI
 * clusters (Budget / Activity / Risk), a Budget Utilization by Cluster
 * bar chart, and a 2-column row with the daily consumption trend chart
 * and a compact open-alerts list.
 *
 * Fuel Requests stats, the Cluster Breakdown table, and the AI Fleet
 * Insights preview that used to live below that in this file are NOT
 * part of the prototype's dashboard screen — each already has its own
 * dedicated page (Fuel Requests, Reconciliation, AI Fleet Insights),
 * reachable from the sidebar, so leaving those out here isn't a loss of
 * functionality, just no longer duplicated on the landing page. Budget
 * Utilization by Cluster was cut for the same reason during that same
 * pass, then restored by request — it's cycle-level budget-vs-consumed
 * context a manager wants without leaving the landing page, same as the
 * three KPI clusters above it.
 *
 * Data sources (all cycle-aware):
 *   /api/diesel/dashboard/kpis/:cycle_key       → KPIs
 *   /api/diesel/dashboard/daily-trend/:ck       → daily consumption chart
 *   /api/diesel-recon/reconciliation/cluster/:ck → per-cluster budget/consumption totals
 *   /api/alerts                                 → open alerts (top 3 shown)
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart, Area, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { useCycles, useDashboard, useDailyTrend, useAlerts, useClusterSummary } from '../../hooks/useDiesel';
import {
  CycleSelector, KpiCard, SectionLabel, Card, AlertCard,
  fmtNum, fmtDate, Spinner, ErrorBox, PageHeader, Btn,
} from '../../components/diesel/DieselComponents';
import { alertsApi, reconciliationApi } from '../../api/dieselApi';
import { useNotification } from '../../contexts/NotificationContext';
import { urgency as urgencyTokens, neutral, font } from '../../styles/dieselTokens';

// Sourced from the shared design tokens rather than their own hex
// literals, so this page's palette can't drift from the rest of the
// redesigned diesel pages.
const GREEN   = urgencyTokens.ok.fg;
const BLUE    = urgencyTokens.medium.fg;
const AMBER   = urgencyTokens.high.fg;
const RED     = urgencyTokens.critical.fg;

// Same thresholds the overall "CMS Consumed" KPI tile above already uses
// (budgetUtilPct > 90 → crit, > 75 → warn) — kept in one place so a
// cluster's bar color and the fleet-wide tile never disagree about what
// counts as "over budget".
function utilizationColor(pct: number): string {
  if (pct > 100) return RED;
  if (pct > 90)  return AMBER;
  return GREEN;
}

export default function DieselDashboardPage() {
  const nav = useNavigate();
  const { success: showSuccess, error: showError } = useNotification();
  const { cycles, selectedKey, setSelectedKey, loading: cyclesLoading } = useCycles();
  const { data: kpiData, loading: kpiLoading, error: kpiError, refetch: refetchKpis } = useDashboard(selectedKey);
  const { data: trendData, loading: trendLoading } = useDailyTrend(selectedKey);
  const { data: alertData, loading: alertLoading, refetch: refetchAlerts } = useAlerts({ status: 'open', limit: 3 });
  const { data: clusterData, loading: clusterLoading } = useClusterSummary(selectedKey);
  const [running, setRunning] = useState(false);

  const kpis  = kpiData?.kpis;
  const alerts = ((alertData as any)?.data || alertData || []).slice(0, 3);
  const trend  = (trendData as any)?.data || trendData || [];
  const clusters = ((clusterData as any)?.data || clusterData || []) as any[];
  const clusterRows = clusters
    .map((c: any) => ({
      cluster: c._id || 'Unassigned',
      budget_l: c.total_budget_liters || 0,
      consumed_l: c.total_cms_consumed || 0,
      utilization_pct: c.total_budget_liters > 0 ? (c.total_cms_consumed / c.total_budget_liters) * 100 : 0,
    }))
    .sort((a, b) => b.utilization_pct - a.utilization_pct);

  async function runReconciliation() {
    if (!selectedKey) return;
    setRunning(true);
    try {
      const res: any = await reconciliationApi.run(selectedKey);
      const result = res?.data || res;
      if (result?.errors > 0) {
        showError(`Reconciliation finished with errors: ${result.processed} site(s) OK, ${result.errors} failed. Check the server logs for details.`);
      } else if (result?.processed === 0) {
        showError(`Reconciliation ran but found no sites with budget, CMS, or field-visit data for ${selectedKey} — nothing to reconcile yet. Upload data for this cycle first.`);
      } else {
        showSuccess(`Reconciliation complete — ${result?.processed ?? 0} site(s) updated for ${selectedKey}.`);
      }
      refetchKpis();
    } catch (e: any) {
      showError(e?.response?.data?.error || e?.message || 'Reconciliation failed — please try again or contact support.');
    } finally {
      setRunning(false);
    }
  }

  async function handleAcknowledge(id: string) { await alertsApi.acknowledge(id); refetchAlerts(); }
  async function handleResolve(id: string) { await alertsApi.resolve(id, 'Resolved from dashboard'); refetchAlerts(); }

  if (cyclesLoading) return <Spinner />;

  const totalBudgetL  = kpis?.total_budget_liters || 0;
  const totalBudgetXAF = kpis?.total_budget_xaf || 0;
  const cmsConsumed   = kpis?.total_cms_consumed || 0;
  const budgetUtilPct = totalBudgetL > 0 ? (cmsConsumed / totalBudgetL) * 100 : 0;

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1400, margin: '0 auto', fontFamily: font.ui }}>
      <PageHeader
        title="Diesel Overview"
        subtitle="Budget, activity and risk for the current reconciliation cycle, at a glance."
        actions={
          <>
            <CycleSelector cycles={cycles} value={selectedKey} onChange={setSelectedKey} />
            <Btn variant="secondary" size="sm" onClick={runReconciliation} disabled={running}>
              {running ? 'Running…' : 'Reconcile'}
            </Btn>
          </>
        }
      />

      {kpiError && <ErrorBox message={kpiError} onRetry={refetchKpis} />}

      {kpiLoading ? <Spinner /> : (
        <>
          <div style={{ marginBottom: 16 }}>
            <SectionLabel>Budget</SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
              <KpiCard label="Budget (L)" value={fmtNum(totalBudgetL)} sub="Cycle allocation" />
              <KpiCard label="Budget (XAF)" value={`${(totalBudgetXAF / 1e6).toFixed(1)}M`}
                sub={`IHS Approved: ${(kpis?.budget_liters_approved_ihs || 0).toLocaleString()} L`} />
              <KpiCard label="CMS Consumed (L)" value={fmtNum(cmsConsumed)}
                sub={`${budgetUtilPct.toFixed(1)}% of budget`}
                tone={budgetUtilPct > 90 ? 'crit' : budgetUtilPct > 75 ? 'warn' : undefined} />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <SectionLabel>Activity</SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
              <KpiCard label="Field-Added Fuel" value={fmtNum(kpis?.total_field_added)} color={BLUE}
                sub={!kpis?.total_sites ? 'Live (reconciliation not yet run)' : undefined} />
              <KpiCard label="Tom Card Spend (L)" value={fmtNum(kpis?.total_tomcard)} color="#7c3aed" />
              <KpiCard label="Total Runtime (h)" value={fmtNum(kpis?.total_final_rh, 1)} sub={`Across ${kpis?.total_sites || 0} sites`} />
            </div>
          </div>

          <div style={{ marginBottom: 24 }}>
            <SectionLabel>Risk</SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
              <KpiCard label="Over-CCPH Sites" value={kpis?.sites_over_ccph || 0}
                tone={(kpis?.sites_over_ccph || 0) > 0 ? 'warn' : undefined} />
              <KpiCard label="Low-Fuel Sites" value={kpis?.sites_low_fuel || 0}
                tone={(kpis?.sites_low_fuel || 0) > 0 ? 'crit' : undefined} />
              <KpiCard label="Theft Suspected" value={kpis?.sites_theft_suspected || 0}
                sub={(kpis?.sites_theft_suspected || 0) > 0 ? 'Under investigation' : undefined}
                tone={(kpis?.sites_theft_suspected || 0) > 0 ? 'crit' : undefined} />
            </div>
          </div>
        </>
      )}

      <div style={{ marginBottom: 14 }}>
        <Card>
          <SectionLabel>Budget utilization by cluster</SectionLabel>
          {clusterLoading ? <Spinner size={28} /> : clusterRows.length === 0 ? (
            <p style={{ color: neutral[400], fontSize: 13, textAlign: 'center', padding: 20 }}>No budget or consumption data reconciled yet for {selectedKey}</p>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={clusterRows} margin={{ left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={neutral[100]} />
                <XAxis dataKey="cluster" tick={{ fontSize: 10 }} angle={-15} textAnchor="end" height={45} />
                <YAxis tick={{ fontSize: 10 }} unit="%" />
                <Tooltip
                  formatter={(v: any, n: string, p: any) =>
                    n === 'Budget Utilization' ? [`${fmtNum(v, 1)}% (${fmtNum(p.payload.consumed_l)} / ${fmtNum(p.payload.budget_l)} L)`, n] : [v, n]
                  }
                />
                <Bar dataKey="utilization_pct" name="Budget Utilization" radius={[4, 4, 0, 0]}>
                  {clusterRows.map((c, i) => (
                    <Cell key={i} fill={utilizationColor(c.utilization_pct)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 14, alignItems: 'start' }}>
        <Card>
          <SectionLabel>Daily consumption trend</SectionLabel>
          {trendLoading ? <Spinner size={28} /> : trend.length === 0 ? (
              <p style={{ color: neutral[400], fontSize: 13, textAlign: 'center', padding: 20 }}>No CMS data uploaded yet for {selectedKey}</p>
            ) : (
              <ResponsiveContainer width="100%" height={190}>
                <AreaChart data={trend}>
                  <defs>
                    <linearGradient id="gCons" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={BLUE}  stopOpacity={0.2} />
                      <stop offset="95%" stopColor={BLUE}  stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gRef" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={GREEN} stopOpacity={0.2} />
                      <stop offset="95%" stopColor={GREEN} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={neutral[100]} />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }}
                    tickFormatter={d => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v: any, n: string) => [fmtNum(v, 0) + ' L', n]} labelFormatter={d => fmtDate(d)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Area type="monotone" dataKey="total_consumed" stroke={BLUE}  fill="url(#gCons)" name="Consumed (L)" strokeWidth={2} />
                  <Area type="monotone" dataKey="total_refuel"   stroke={GREEN} fill="url(#gRef)"  name="Refuel (L)"  strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            )}
        </Card>

        <Card>
          <SectionLabel>Open alerts</SectionLabel>
          {alertLoading ? <Spinner size={24} /> : alerts.length === 0 ? (
            <p style={{ color: GREEN, textAlign: 'center', padding: 20, fontSize: 13 }}>No open alerts</p>
          ) : (
            alerts.map((a: any) => (
              <AlertCard key={a._id} alert={a} compact onAcknowledge={handleAcknowledge} onResolve={handleResolve} />
            ))
          )}
          <div style={{ textAlign: 'center', marginTop: 10 }}>
            <Btn variant="ghost" size="sm" onClick={() => nav('/diesel/alerts')}>View all alerts →</Btn>
          </div>
        </Card>
      </div>
    </div>
  );
}
